import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { withQuery } from '@/lib/qs';

import { useApiClient } from '../ApiClientProvider';
import { queryKeys } from '../keys';
import type { Paginated } from '../types/common';
import type {
  OrderDetails,
  OrderShipmentFilters,
  OrderWithShipments,
} from '../types/order-shipments';
import type { Order } from '../types/orders';

type Wrapped<T> = { data: T };

export function useOrderShipments(filters: OrderShipmentFilters) {
  const api = useApiClient();
  return useQuery<Paginated<OrderWithShipments>, Error>({
    queryKey: queryKeys.orderShipments.list(filters),
    queryFn: ({ signal }) => api.get(withQuery('/admin/order-shipments', filters), signal),
    // New orders arrive via Shopify webhooks (no client mutation to invalidate
    // on), so the global 5-min staleTime would serve a stale list. Always
    // refetch on mount and when the merchant tabs back into the admin.
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

/** `shopifyOrderId` - order detail URLs carry the Shopify order id, not the internal row id. */
export function useOrderDetails(shopifyOrderId: number | string | undefined) {
  const api = useApiClient();
  return useQuery<OrderDetails, Error>({
    queryKey: queryKeys.orderShipments.detail(shopifyOrderId ?? 'none'),
    queryFn: async ({ signal }) => {
      const response = await api.get<Wrapped<OrderDetails>>(
        `/admin/order-shipments/${shopifyOrderId}`,
        signal,
      );
      return response.data;
    },
    enabled: shopifyOrderId !== undefined && shopifyOrderId !== null,
    // Webhook race: the merchant may follow the Shopify admin link before the
    // order/create webhook has finished processing. Retry with backoff.
    retry: 3,
    retryDelay: (attempt) => Math.min(800 * 2 ** attempt, 4000),
  });
}

export function useUpdateOrderSelectedPoint(orderId: number) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<Order, Error, { point_id: string }>({
    mutationFn: async (payload) => {
      const response = await api.patch<Wrapped<Order>>(
        `/admin/orders/${orderId}/selected-point`,
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.orderShipments.all });
      void qc.invalidateQueries({ queryKey: queryKeys.orders.all });
    },
  });
}
